/**
 * WebGLRenderer — PixiJS-backed renderer for the live game.
 *
 * This file is the orchestrator and public Renderer interface.
 * All rendering logic lives in render/webgl/ sub-modules:
 *   render/webgl/background.js  — nebula fullscreen quad + breakout tiles
 *   render/webgl/planets.js     — procedural planet shader + spawner sprites
 *   render/webgl/fighters.js    — fighter/exhaust/explosion/fireworks instanced meshes
 *   render/webgl/units.js       — icon ships, pickups, decorations, speed zones
 *   render/webgl/overlays.js    — attack lines, frozen borders, power range, VFX
 *
 * All per-frame state lives in WebGLRenderer fields (prefixed _); sub-modules
 * receive `this` as first argument `wr` and read/write those fields directly,
 * the same pattern as Canvas2D (functions take renderer `r`).
 *
 * Canvas/layer layout:
 *   - fgCanvas:      PixiJS Application (WebGL2)
 *   - bgCanvas:      Canvas2D via _inner
 *   - effectsCanvas: hidden (unused in WebGL path)
 *   - uiCanvas:      Canvas2D via HudPainter
 */

import { Canvas2DRenderer } from './renderer.js';
import { buildWebGLEffectFactories } from './render/webgl/effects.js';
import { setPixiClasses, Matrix, RenderTexture, releaseCanvasTexture, clearTexCache } from './render/webgl/pixi.js';

import { buildNebulaBackground, renderNebulaRT, renderBackground } from './render/webgl/background.js';
import { buildPlanetShader, syncSpawners } from './render/webgl/planets.js';
import {
    buildFighterMesh, buildFireworksMesh, buildExplosionMesh, drawFighters,
} from './render/webgl/fighters.js';
import {
    syncIconUnits, syncPickups, syncDecorLayer,
    syncSpeedZones, syncPixiMidTopEffects,
} from './render/webgl/units.js';
import { drawAttackLines, syncOverlays, syncPixiEffects } from './render/webgl/overlays.js';

import {
    FIGHTER_FLOATS, MAX_FIGHTERS,
    EXPLOSION_FLOATS, MAX_EXPLOSIONS,
    FIREWORKS_FLOATS, MAX_FIREWORKS,
} from './render/webgl/shaders.js';

/**
 * WebGL renderer backend — wraps PixiJS for GPU-accelerated game-world rendering.
 * Implements the Renderer interface; composes HudPainter for the UI layer.
 * @implements {import('./render/interface.js').Renderer}
 */
class WebGLRenderer {
    /**
     * @param {{bg:HTMLCanvasElement, fg:HTMLCanvasElement, ui:HTMLCanvasElement, effects:HTMLCanvasElement|null}} canvases
     * @param {import('./game.js').Game} game
     * @param {import('./ui.js').UI} ui
     * @param {import('./util.js').EventFeed} eventFeed
     * @param {import('./util.js').PerformanceOverlay} performanceOverlay
     */
    constructor(canvases, game, ui, eventFeed, performanceOverlay) {
        const refCanvas = canvases.fg;
        const glCanvas  = document.createElement('canvas');
        glCanvas.width  = refCanvas.width;
        glCanvas.height = refCanvas.height;
        glCanvas.className = refCanvas.className;
        glCanvas.style.cssText = refCanvas.style.cssText;
        refCanvas.parentNode?.insertBefore(glCanvas, refCanvas.nextSibling);
        refCanvas.style.opacity = '0';
        glCanvas.style.pointerEvents = 'none';
        this._glCanvas = glCanvas;
        this._fgCanvas = refCanvas;

        // Recover from GL context loss (mobile GPU resets) — without this the
        // game becomes a permanent black screen. preventDefault marks the
        // context restorable; the rebuild is deferred because handleContextLost
        // destroys this renderer (and the canvas this event fired on).
        this._onContextLost = (e) => {
            e.preventDefault();
            setTimeout(() => game.handleContextLost?.(), 0);
        };
        glCanvas.addEventListener('webglcontextlost', this._onContextLost);

        /** @type {import('./render/interface.js').RendererType} */
        this.type = 'webgl';
        this.inputCanvas   = refCanvas;
        this.displayCanvas = glCanvas;

        const offscreenFg = (() => {
            const c = document.createElement('canvas');
            c.width = refCanvas.width; c.height = refCanvas.height;
            return c;
        })();

        /** @type {import('./game.js').Game} */
        this.game = game;

        /** @type {Canvas2DRenderer} */
        this._inner = new Canvas2DRenderer(
            { bg: canvases.bg, fg: offscreenFg, ui: canvases.ui },
            game, ui, eventFeed, performanceOverlay
        );
        // (The inner Canvas2D constructor registers its factories on the
        // shared EffectSystem; our own registration below replaces them.)

        /** @type {HudPainter} Shared with the inner Canvas2D renderer — same ui
         *  canvas/context and the inner setScaleFactor keeps its state in sync. */
        this._hud = this._inner._hud;

        this._effectsCanvas = canvases.effects ?? null;
        this._effectsCtx    = this._effectsCanvas?.getContext('2d') ?? null;
        this._bgCanvas      = canvases.bg ?? null;

        // ---- Pixi state (populated by _initPixi) ----
        this._pixi            = null;
        this._worldRT         = null;
        this._worldSprite     = null;
        this._worldContainer  = null;
        this._zoomContainer   = null;

        // Background
        this._nebulaMesh         = null;
        this._nebulaUniforms     = null;
        this._nebulaTime         = 0;
        this._nebulaLevelId      = null;
        this._nebulaRT           = null;
        this._nebulaSprite       = null;
        this._nebulaFrameCounter = 0;
        this._breakoutTileGraphics = null;

        // Fighters / exhaust
        this._fighterGraphics  = null;
        this._exhaustMesh      = null;
        this._exhaustGeo       = null;
        this._exhaustShader    = null;
        this._shipExhaustMesh  = null;
        this._shipExhaustGeo   = null;
        this._shipExhaustBuf   = null;
        this._fighterMesh      = null;
        this._fighterBuf       = null;
        this._fighterGeo       = null;
        this._fighterShader    = null;
        this._uMData           = new Float32Array(9);
        this._fighterData      = new Float32Array(MAX_FIGHTERS  * FIGHTER_FLOATS);
        this._shipData         = new Float32Array(512           * FIGHTER_FLOATS);

        // Speed zones / effects
        this._speedZoneContainer        = null;
        this._speedZonePool             = new Map();
        this._groundIconEffectContainer = null;
        this._topIconEffectContainer    = null;
        this._midEffectGraphics         = null;
        /** @type {import('./render/webgl/effects.js').WebGLIconEffect[]} */
        this._iconEffects               = [];
        /** @type {import('./render/webgl/effects.js').WebGLPowerImpact[]} */
        this._powerImpacts              = [];

        // Layers
        this._progressBarLayer  = null;
        this._underlayGraphics  = null;
        this._underlayLayer     = null;
        this._iconLayer         = null;
        this._overlayLayer      = null;
        this._overlayGraphics2  = null;
        this._underlayPool      = new Map();
        this._overlayPool       = new Map();
        this._spawnerLayer      = null;
        this._attackGlowGraphics  = null;
        this._attackLineGraphics  = null;
        this._overlayGraphics     = null;
        this._effectsLayer        = null;

        /** @type {import('./render/webgl/effects.js').WebGLExplosion[]} */
        this._explosions = [];
        /** @type {import('./render/webgl/effects.js').WebGLFireworks[]} */
        this._fireworks  = [];

        // Sprite/texture pools
        this._iconPool         = new Map();
        this._pickupPool       = new Map();
        this._pickupTexCache   = new Map();
        this._spawnerPool      = new Map();
        this._planetTexCache   = new Map();
        this._spawnerSizeRange = null;
        this._planetMeshRocky  = null;
        this._planetMeshGas    = null;
        this._planetUniforms   = null;

        // Explosions / fireworks
        this._explosionLayer    = null;
        this._explosionMesh     = null;
        this._explosionGeo      = null;
        this._explosionBuf      = null;
        this._explosionData     = new Float32Array(MAX_EXPLOSIONS * EXPLOSION_FLOATS);
        this._explosionUniforms = null;
        this._fireworksMesh     = null;
        this._fireworksGeo      = null;
        this._fireworksBuf      = null;
        this._fireworksData     = new Float32Array(MAX_FIREWORKS  * FIREWORKS_FLOATS);
        this._fireworksUniforms = null;

        this._zoom        = 1;
        /** @type {{[key: string]: boolean}} */
        this._layerVisible = {};

        // One-time factory registration (replaces the wrapped Canvas2D
        // renderer's): Game's EffectSystem lives as long as the Game. The
        // onClear hook lets loadLevel drop our per-level GPU effect pools.
        game.effectSystem?.setFactories(
            buildWebGLEffectFactories(this), () => this._clearPerLevelState());

        if (canvases.bg)         canvases.bg.style.display         = 'none';
        if (this._effectsCanvas) this._effectsCanvas.style.display = 'none';

        /** @type {boolean} Set by destroy(); _initPixi bails at its next await. */
        this._destroyed = false;
        // Init is async and can fail — Safari refusing to create a GL context
        // (LORD-OF-THE-SWARM-P), a renderer torn down while its init was still
        // in flight (LORD-OF-THE-SWARM-R). Left alone, that is an unhandled
        // rejection nobody acts on, and the player keeps a black world under
        // a live HUD. Route it to the game so it can fall back to Canvas2D.
        this._initPromise = this._initPixi().catch((err) => this._onInitFailed(err));
    }

    /**
     * Init failed. A renderer destroyed mid-init is not a failure — the game
     * already moved on — so only a live renderer reports and falls back.
     * @param {unknown} err
     */
    _onInitFailed(err) {
        if (this._destroyed) return;
        console.error('[WebGLRenderer] Pixi init failed — falling back to Canvas2D.', err);
        // Handled here, but still worth a (warning) report: how often WebGL is
        // unavailable in the wild is exactly what decides the default backend.
        try {
            window.Sentry?.captureException?.(err, { level: 'warning', tags: { phase: 'webgl-init' } });
        } catch { /* error tracking may not be loaded */ }
        this.game.handleWebGLInitFailed?.(this);
    }

    // -----------------------------------------------------------------------
    // Live effectSystem
    // -----------------------------------------------------------------------
    // Read live off `game` — a pure getter, like gridSystem/entitySystem.
    // The instance is stable for the Game's lifetime; factory registration
    // happens once in the constructor, and loadLevel's effectSystem.clear()
    // drops our per-level GPU state via the onClear hook.

    get effectSystem() { return this.game.effectSystem; }

    /** Drop all per-level effect + sprite-pool state. @private */
    _clearPerLevelState() {
        this._explosions.length = 0;
        this._fireworks.length  = 0;
        for (const eff of this._iconEffects) eff.cleanup();
        this._iconEffects.length  = 0;
        this._powerImpacts.length = 0;
        if (this._effectsLayer)      this._effectsLayer.clear();
        if (this._midEffectGraphics) this._midEffectGraphics.clear();

        this._speedZonePool.forEach((sprite, zone) => {
            if (this._speedZoneContainer) this._speedZoneContainer.removeChild(sprite);
            sprite.destroy({ texture: false });
            releaseCanvasTexture(zone._gradCanvas);
            delete zone._gradCanvas;
            delete zone._gradCacheKey;
        });
        this._speedZonePool.clear();

        this._spawnerPool.forEach(entry => {
            entry.container.destroy({ children: true });
            if (entry.progressBar && this._progressBarLayer) {
                this._progressBarLayer.removeChild(entry.progressBar);
                entry.progressBar.destroy();
            }
        });
        this._spawnerPool.clear();
        this._iconPool.forEach(s => s.destroy({ texture: false }));
        this._iconPool.clear();
        this._pickupPool.forEach(s => s.destroy({ texture: false }));
        this._pickupPool.clear();

        const destroyDecoPool = (pool, layer) => {
            pool.forEach((sprites) => {
                for (let k = 0; k < sprites.length; k++) {
                    if (sprites[k]) { layer?.removeChild(sprites[k]); sprites[k].destroy({ texture: false }); }
                }
            });
            pool.clear();
        };
        destroyDecoPool(this._underlayPool, this._underlayLayer);
        destroyDecoPool(this._overlayPool,  this._overlayLayer);

        this._planetTexCache.forEach(entry => entry.rt?.destroy(true));
        this._planetTexCache.clear();
        this._spawnerSizeRange = null;
    }

    // -----------------------------------------------------------------------
    // Async PixiJS init — scene graph assembly
    // -----------------------------------------------------------------------

    async _initPixi() {
        const pixi = await import('pixi.js');
        if (this._destroyed) return;
        setPixiClasses(pixi);

        // Pixi builds its uniform-sync and shader-sync functions with
        // `new Function`, which the deployed CSP forbids (script-src carries no
        // 'unsafe-eval' — public/_headers). Without the shim below,
        // Application.init() throws from _unsafeEvalCheck, this init promise
        // rejects, and the world never draws while the Canvas2D HUD lives on —
        // the production-only black screen. Pixi ships eval-free polyfills for
        // exactly this. They are loaded unconditionally: this used to probe
        // `new Function('')` first and only shim where it threw, but the probe
        // itself is a CSP violation, so every WebGL start in production filed
        // a "Blocked 'script' from 'eval:'" report with Sentry (LORD-OF-THE-
        // SWARM-9, ~250/day) for a path that was going to take the shim
        // anyway. Installing the shim up front also replaces Pixi's own
        // internal eval probe, so no environment attempts eval at all, and
        // dev now renders through the same code path as production.
        await import('pixi.js/unsafe-eval');
        if (this._destroyed) return;

        const { Application, Container, Graphics, BlurFilter, Sprite } = pixi;
        // RenderTexture and Matrix are used below via the live-binding imports from pixi.js.

        const app = new Application();
        await app.init({
            canvas:          this._glCanvas,
            width:           this._glCanvas.width,
            height:          this._glCanvas.height,
            antialias:       true,
            backgroundAlpha: 0,
            preference:      'webgl',
            autoDensity:     false,
            resolution:      1,
        });
        // destroy() ran while init was awaiting: it found no app to tear down,
        // so this one is ours to release. Everything after this dereferences
        // the canvas destroy() already removed (LORD-OF-THE-SWARM-R).
        if (this._destroyed) {
            try { app.destroy(true); } catch { /* context already gone */ }
            return;
        }
        this._pixi = app;
        app.ticker.stop();

        // Nebula background
        buildNebulaBackground(this);
        app.stage.addChild(this._nebulaSprite);

        // World RenderTexture
        const cW = this._glCanvas.width;
        const cH = this._glCanvas.height;
        this._worldRT = RenderTexture.create({ width: cW, height: cH, resolution: 1 });
        this._worldSprite = new Sprite(this._worldRT);
        this._worldSprite.width  = cW;
        this._worldSprite.height = cH;
        app.stage.addChild(this._worldSprite);

        this._worldContainer = new Container();

        const W = this._inner.gridSystem.width;
        const H = this._inner.gridSystem.height;
        const RING_BLEED = 100;
        const clipMask = new Graphics()
            .rect(-RING_BLEED, -RING_BLEED, W + 2 * RING_BLEED, H + 2 * RING_BLEED)
            .fill(0xFFFFFF);
        this._worldContainer.addChild(clipMask);
        this._worldContainer.mask = clipMask;

        this._zoomContainer = new Container();
        this._worldContainer.addChild(this._zoomContainer);

        // Z-order (bottom to top):
        //   speedZone → spawners → groundIconFx → exhausts → underlays → pickups → icons →
        //   overlays → fighters → midFx → attackGlow → attackLines → topIconFx →
        //   fireworks → explosions → progressBars

        this._breakoutTileGraphics = new Graphics();
        this._zoomContainer.addChild(this._breakoutTileGraphics);

        this._speedZoneContainer = new Container();
        this._zoomContainer.addChild(this._speedZoneContainer);

        this._spawnerLayer = new Container();
        this._zoomContainer.addChild(this._spawnerLayer);

        this._groundIconEffectContainer = new Container();
        this._zoomContainer.addChild(this._groundIconEffectContainer);

        buildFighterMesh(this);
        buildPlanetShader(this);

        this._zoomContainer.addChild(this._exhaustMesh);
        this._zoomContainer.addChild(this._shipExhaustMesh);

        this._underlayGraphics = new Graphics();
        this._zoomContainer.addChild(this._underlayGraphics);
        this._underlayLayer = new Container();
        this._zoomContainer.addChild(this._underlayLayer);

        this._pickupLayer = new Container();
        this._zoomContainer.addChild(this._pickupLayer);

        this._iconLayer = new Container();
        this._zoomContainer.addChild(this._iconLayer);

        this._overlayLayer = new Container();
        this._zoomContainer.addChild(this._overlayLayer);
        this._overlayGraphics2 = new Graphics();
        this._zoomContainer.addChild(this._overlayGraphics2);

        this._zoomContainer.addChild(this._fighterMesh);

        this._fighterGraphics = new Graphics();
        this._zoomContainer.addChild(this._fighterGraphics);

        this._midEffectGraphics = new Graphics();
        this._zoomContainer.addChild(this._midEffectGraphics);

        this._attackGlowGraphics = new Graphics();
        this._attackGlowGraphics.filters = [new BlurFilter({ strength: 10, quality: 3 })];
        this._zoomContainer.addChild(this._attackGlowGraphics);

        this._attackLineGraphics = new Graphics();
        this._zoomContainer.addChild(this._attackLineGraphics);

        this._topIconEffectContainer = new Container();
        this._zoomContainer.addChild(this._topIconEffectContainer);

        this._effectsLayer = new Graphics();
        this._zoomContainer.addChild(this._effectsLayer);

        buildFireworksMesh(this);
        buildExplosionMesh(this);
        this._explosionLayer = new Container();
        this._explosionLayer.addChild(this._fireworksMesh);
        this._explosionLayer.addChild(this._explosionMesh);
        this._zoomContainer.addChild(this._explosionLayer);

        this._progressBarLayer = new Container();
        this._zoomContainer.addChild(this._progressBarLayer);

        this._overlayGraphics = new Graphics();
        this._worldContainer.addChild(this._overlayGraphics);

        // Catch-up resize + transform after async gap.
        const initW = this._glCanvas.width;
        const initH = this._glCanvas.height;
        this._pixi.renderer.resize(initW, initH);
        if (this._worldRT && (this._worldRT.width !== initW || this._worldRT.height !== initH)) {
            this._worldRT.destroy(true);
            this._worldRT = RenderTexture.create({ width: initW, height: initH, resolution: 1 });
            this._worldSprite.texture = this._worldRT;
            this._worldSprite.width   = initW;
            this._worldSprite.height  = initH;
        }
        this._applyWorldTransform();

        for (const { key } of WebGLRenderer.LAYER_DEFS) {
            if (key in this._layerVisible) {
                this._applyLayerVisible(key, this._layerVisible[key]);
            }
        }
    }

    // -----------------------------------------------------------------------
    // World-to-screen transform
    // -----------------------------------------------------------------------

    _applyWorldTransform() {
        if (!this._worldContainer) return;
        const [a, b, c, d, e, f] = this._inner._gameTf;
        // Matrix is a live binding from pixi.js — set by setPixiClasses in _initPixi.
        this._worldContainer.setFromMatrix(new Matrix(a, b, c, d, e, f));
    }

    // -----------------------------------------------------------------------
    // Layer visibility toggles
    // -----------------------------------------------------------------------

    /** @type {Array<{label: string, key: string}>} */
    static LAYER_DEFS = [
        { label: 'Nebula',         key: 'nebula'      },
        { label: 'Planets',        key: 'planets'     },
        { label: 'Exhaust',        key: 'exhaust'     },
        { label: 'Icons',          key: 'icons'       },
        { label: 'Fighters',       key: 'fighters'    },
        { label: 'Attack glow',    key: 'attackGlow'  },
        { label: 'Attack lines',   key: 'attackLines' },
        { label: 'Explosions',     key: 'explosions'  },
    ];

    get layerDefs() { return WebGLRenderer.LAYER_DEFS; }

    whenReady() { return this._initPromise ?? Promise.resolve(); }

    toggleLayer(key) {
        const next = !(this._layerVisible[key] ?? true);
        this._layerVisible[key] = next;
        this._applyLayerVisible(key, next);
        return next;
    }

    getLayerStates() {
        return WebGLRenderer.LAYER_DEFS.map(({ label, key }) => ({
            label, visible: this._layerVisible[key] ?? true,
        }));
    }

    _applyLayerVisible(key, visible) {
        switch (key) {
            case 'nebula':      if (this._nebulaSprite)      this._nebulaSprite.visible     = visible; break;
            case 'planets':     if (this._spawnerLayer)      this._spawnerLayer.visible     = visible; break;
            case 'exhaust':
                if (this._exhaustMesh)     this._exhaustMesh.visible     = visible;
                if (this._shipExhaustMesh) this._shipExhaustMesh.visible = visible; break;
            case 'icons':       if (this._iconLayer)         this._iconLayer.visible        = visible; break;
            case 'fighters':
                if (this._fighterMesh)     this._fighterMesh.visible     = visible;
                if (this._fighterGraphics) this._fighterGraphics.visible = visible; break;
            case 'attackGlow':  if (this._attackGlowGraphics) this._attackGlowGraphics.visible = visible; break;
            case 'attackLines': if (this._attackLineGraphics) this._attackLineGraphics.visible = visible; break;
            case 'explosions':
                if (this._effectsLayer)   this._effectsLayer.visible    = visible;
                if (this._explosionLayer) this._explosionLayer.visible  = visible; break;
        }
    }

    // -----------------------------------------------------------------------
    // Public Renderer interface
    // -----------------------------------------------------------------------

    get gridSystem()       { return this._inner.gridSystem; }
    get entitySystem()     { return this._inner.entitySystem; }
    /** @param {import('./util.js').EntitySystem|null} es */
    setEntitySystemOverride(es) { this._inner.setEntitySystemOverride(es); }

    get canvas()    { return this._fgCanvas; }
    get bgCanvas()  { return this._inner.bgCanvas; }
    get uiCanvas()  { return this._inner.uiCanvas; }
    get context()   { return null; }
    get bgContext() { return this._inner.bgContext; }
    get uiContext() { return this._inner.uiContext; }

    get gameOffsetX()          { return this._inner.gameOffsetX; }
    get gameOffsetY()          { return this._inner.gameOffsetY; }
    get gameOffsetYCentering() { return this._inner.gameOffsetYCentering; }
    get _scaleFactor()         { return this._inner._scaleFactor; }

    setScaleFactor(factor, portrait) {
        this._inner.setScaleFactor(factor, portrait);  // also updates the shared _hud

        const of = this._inner.canvas;
        const rf = this._fgCanvas;
        if (of.width !== rf.width || of.height !== rf.height) {
            of.width  = rf.width;
            of.height = rf.height;
        }

        if (this._glCanvas) {
            // Assigning width/height resets the GL drawing buffer even when the
            // value is unchanged — that blanks the world until something paints
            // again, which a paused game never does. Only touch a real change.
            if (this._glCanvas.width !== rf.width || this._glCanvas.height !== rf.height) {
                this._glCanvas.width  = rf.width;
                this._glCanvas.height = rf.height;
            }
            this._glCanvas.style.cssText = rf.style.cssText;
            this._glCanvas.style.opacity       = '1';
            this._glCanvas.style.pointerEvents = 'none';
        }

        if (this._pixi) {
            const cW = rf.width;
            const cH = rf.height;
            this._pixi.renderer.resize(cW, cH);
            this._applyWorldTransform();

            if (this._worldRT && (this._worldRT.width !== cW || this._worldRT.height !== cH)) {
                this._worldRT.destroy(true);
                this._worldRT = RenderTexture.create({ width: cW, height: cH, resolution: 1 });
                this._worldSprite.texture = this._worldRT;
                this._worldSprite.width   = cW;
                this._worldSprite.height  = cH;
            }

            if (this._nebulaRT && (this._nebulaRT.width !== cW || this._nebulaRT.height !== cH)) {
                this._nebulaRT.destroy(true);
                this._nebulaRT = RenderTexture.create({ width: cW, height: cH, resolution: 1 });
                this._nebulaSprite.texture = this._nebulaRT;
                renderNebulaRT(this);
            }
        }
    }

    /** @param {number} dt */
    renderBackground(dt) {
        renderBackground(this, dt);
    }

    renderGameLayer(dt) {
        if (!this._pixi) return;

        const zoom = this._inner.game.getLevelZoomScale ? this._inner.game.getLevelZoomScale() : 1;
        if (zoom !== this._zoom) {
            this._zoom = zoom;
            const W = this._inner.gridSystem.width;
            const H = this._inner.gridSystem.height;
            if (zoom === 1) {
                this._zoomContainer.setFromMatrix(new Matrix());
            } else {
                const m = new Matrix();
                m.translate(-W / 2, -H / 2);
                m.scale(zoom, zoom);
                m.translate(W / 2, H / 2);
                this._zoomContainer.setFromMatrix(m);
            }
        }

        const units = this._inner.entitySystem.units;
        const time  = this._inner.entitySystem.time;

        syncSpeedZones(this, time);
        drawFighters(this);
        syncSpawners(this);

        if (this._underlayGraphics) this._underlayGraphics.clear();
        if (this._overlayGraphics2) this._overlayGraphics2.clear();
        syncDecorLayer(this, units, this._underlayPool, this._underlayLayer,
            this._underlayGraphics, 'underlays', dt, time);
        syncPickups(this);
        syncIconUnits(this);
        syncDecorLayer(this, units, this._overlayPool, this._overlayLayer,
            this._overlayGraphics2, 'overlays', dt, time, 2 / 3);

        drawAttackLines(this);
        syncPixiEffects(this, dt);
        syncPixiMidTopEffects(this, dt);
        syncOverlays(this);

        const pixiRenderStart = performance.now();
        if (this._worldRT) {
            this._pixi.renderer.render({
                container: this._worldContainer,
                target:    this._worldRT,
                clear:     true,
            });
        }
        this._pixi.renderer.render(this._pixi.stage);
        this._inner.game.entityRenderTimeMs =
            this._inner.game.entityRenderTimeMs * 0.9 + (performance.now() - pixiRenderStart) * 0.1;
    }

    renderUiLayer() {
        this._hud.renderAll();
    }

    /** @returns {ReturnType<Canvas2DRenderer['getWorldTransform']>} */
    getWorldTransform() {
        return this._inner.getWorldTransform();
    }

    destroy() {
        this._destroyed = true;
        // Drop every cached canvas texture before the app goes away, otherwise
        // the module-level cache pins all canvases and textures across backend
        // switches. They are lazily recreated if WebGL comes back.
        clearTexCache();
        this._pickupTexCache.clear();
        // Drop the context-loss listener before tearing the app down: destroying
        // the Pixi app loses the GL context on purpose, and that event must not
        // be read as a GPU reset and answered with a rebuild.
        this._glCanvas?.removeEventListener('webglcontextlost', this._onContextLost);
        if (this._pixi) {
            try { this._pixi.destroy(true); } catch { /* ignore if already gone */ }
            this._pixi = null;
        }
        if (this._glCanvas?.parentNode) {
            this._glCanvas.parentNode.removeChild(this._glCanvas);
        }
        this._glCanvas = null;
        if (this._fgCanvas)      this._fgCanvas.style.opacity = '';
        if (this._effectsCanvas) this._effectsCanvas.style.display = '';
        if (this._bgCanvas)      this._bgCanvas.style.display = '';
    }
}

export { WebGLRenderer };
